import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Bot } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AwaChat = () => {
  const { language } = useLanguage();
  const t = language === 'fr';
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [showFaq, setShowFaq] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const isFirstOpen = useRef(true);

  // FAQ questions
  const faqQuestions = t ? [
    { icon: '🍎', text: 'Quels fruits avez-vous ?' },
    { icon: '🥬', text: 'Quels légumes disponibles ?' },
    { icon: '🚚', text: 'Délais de livraison ?' },
    { icon: '📋', text: 'Comment passer commande ?' },
    { icon: '💰', text: 'Demander un devis' },
    { icon: '📍', text: 'Où êtes-vous situés ?' }
  ] : [
    { icon: '🍎', text: 'What fruits do you have?' },
    { icon: '🥬', text: 'What vegetables available?' },
    { icon: '🚚', text: 'Delivery times?' },
    { icon: '📋', text: 'How to place an order?' },
    { icon: '💰', text: 'Request a quote' },
    { icon: '📍', text: 'Where are you located?' }
  ];

  // Scroll to bottom only after user sends a message (not on first open)
  useEffect(() => {
    if (messages.length > 1 && !isFirstOpen.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    if (messages.length === 1) {
      isFirstOpen.current = false;
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Initialize conversation when chat opens
  const initConversation = async () => {
    if (conversationId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/awa/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: 'customer', channel: 'web' })
      });
      const data = await response.json();
      setConversationId(data.conversation_id);
      
      // Add welcome message
      setMessages([{
        role: 'assistant',
        content: t 
          ? 'Bonjour ! Je suis Awa, votre assistante Océana. Comment puis-je vous aider aujourd\'hui ? 🍎🥬'
          : 'Hello! I\'m Awa, your Océana assistant. How can I help you today? 🍎🥬'
      }]);
    } catch (error) {
      console.error('Failed to initialize conversation:', error);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    initConversation();
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading || !conversationId) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setShowFaq(false);
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/awa/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage })
      });

      if (!response.ok) throw new Error('Failed to send message');

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';
      let buffer = '';

      // Add empty assistant message
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            
            assistantMessage += data;
            setMessages(prev => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1] = {
                role: 'assistant',
                content: assistantMessage
              };
              return newMessages;
            });
          }
        }
      }

      // Process any remaining buffer
      if (buffer.startsWith('data: ')) {
        const data = buffer.slice(6);
        if (data !== '[DONE]') {
          assistantMessage += data;
          setMessages(prev => {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = {
              role: 'assistant',
              content: assistantMessage
            };
            return newMessages;
          });
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t 
          ? 'Désolée, une erreur s\'est produite. Veuillez réessayer.'
          : 'Sorry, an error occurred. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleFaqClick = (question) => {
    setShowFaq(false);
    setInputValue(question);
    // Petit délai pour que l'input soit visible avant l'envoi
    setTimeout(() => {
      sendMessageWithText(question);
    }, 100);
  };

  const sendMessageWithText = async (text) => {
    if (!text.trim() || isLoading || !conversationId) return;

    setInputValue('');
    setShowFaq(false);
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/awa/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text })
      });

      if (!response.ok) throw new Error('Failed to send message');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';
      let buffer = '';

      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            
            assistantMessage += data;
            setMessages(prev => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1] = {
                role: 'assistant',
                content: assistantMessage
              };
              return newMessages;
            });
          }
        }
      }

      // Process remaining buffer
      if (buffer.startsWith('data: ')) {
        const data = buffer.slice(6);
        if (data !== '[DONE]') {
          assistantMessage += data;
          setMessages(prev => {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = {
              role: 'assistant',
              content: assistantMessage
            };
            return newMessages;
          });
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t 
          ? 'Désolée, une erreur s\'est produite. Veuillez réessayer.'
          : 'Sorry, an error occurred. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={handleOpen}
            className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-[#1A5C38] text-white rounded-full shadow-lg hover:bg-[#1A5C38]/90 transition-all flex items-center justify-center group"
            data-testid="awa-chat-button"
          >
            <MessageCircle size={28} />
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#F5A623] rounded-full flex items-center justify-center text-xs font-bold">
              1
            </span>
            {/* Tooltip */}
            <span className="absolute right-full mr-3 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {t ? 'Discuter avec Awa' : 'Chat with Awa'}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
            data-testid="awa-chat-window"
          >
            {/* Header */}
            <div className="bg-[#1A5C38] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Bot size={22} />
                </div>
                <div>
                  <h3 className="font-semibold text-[#F5A623]">Awa</h3>
                  <p className="text-xs text-white/70">
                    {t ? 'Assistante Océana' : 'Océana Assistant'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                data-testid="awa-chat-close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-[#1A5C38] text-white rounded-br-md'
                        : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <div className="text-sm prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0.5">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}
              
              {/* FAQ Buttons - shown after welcome message */}
              {showFaq && messages.length === 1 && !isLoading && (
                <div className="mt-4">
                  <p className="text-xs text-gray-500 mb-3 text-center">
                    {t ? 'Questions fréquentes :' : 'Frequently asked:'}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {faqQuestions.map((faq, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleFaqClick(faq.text)}
                        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl text-left text-xs text-gray-700 hover:bg-[#1A5C38]/5 hover:border-[#1A5C38]/30 transition-all"
                      >
                        <span>{faq.icon}</span>
                        <span className="line-clamp-1">{faq.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
                <div className="flex justify-start">
                  <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                    <Loader2 className="w-5 h-5 animate-spin text-[#1A5C38]" />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 bg-white border-t border-gray-200">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t ? 'Écrivez votre message...' : 'Type your message...'}
                  className="flex-1 px-4 py-3 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#1A5C38]/20"
                  disabled={isLoading}
                  data-testid="awa-chat-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  className="w-11 h-11 bg-[#1A5C38] text-white rounded-full flex items-center justify-center hover:bg-[#1A5C38]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="awa-chat-send"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send size={18} />
                  )}
                </button>
              </div>
              <p className="text-[10px] text-gray-400 text-center mt-2">
                Awa by Océana • Powered by AI
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AwaChat;
